<template>
  <div
    class="side-navigation"
    :class="{ visible: isSideMenuVisible, collapsed: isMainMenuCollapsed }"
    @focusout="onNavFocusOut"
    @keydown.esc="onNavEscape"
  >
    <div class="side-navigation-close" data-dd-action-name="menu:close" @click="toggleSideMenu">&#x2715;</div>
    <div class="side-navigation-logo d-none d-md-block">
      <img
        v-if="isMainMenuCollapsed"
        :src="`${baseUrl}/img/flexigo-logo-min.png`"
        :srcset="`${baseUrl}/img/flexigo-logo-min.png, ${baseUrl}/img/flexigo-logo-min@2x.png, ${baseUrl}/img/flexigo-logo-min@3x.png`"
        alt="Flexigo"
      />
      <img
        v-else
        :src="`${baseUrl}/img/flexigo-logo-white.png`"
        :srcset="`${baseUrl}/img/flexigo-logo-white.png, ${baseUrl}/img/flexigo-logo-white@2x.png, ${baseUrl}/img/flexigo-logo-white@3x.png`"
        alt="Flexigo"
      />
    </div>

    <!-- mobile only logout link -->
    <div class="side-navigation-logout d-md-none">
      {{ fullName }} <br />
      <a class="" href="#" data-dd-action-name="menu:logout" @click.prevent="logout">{{
        $t('flexigo.employee.dashboard.signOut')
      }}</a>
    </div>

    <ul class="side-navigation-menu-items">
      <li
        v-for="(item, index) in menuChildren"
        :key="`${index}-${item.text}`"
        @mouseenter="onItemActivate($event, item, index)"
        @mouseleave="scheduleFlyoutClose"
        @focusin="onItemActivate($event, item, index)"
      >
        <a
          v-if="item.children"
          class="collapsed"
          href=""
          :aria-haspopup="isRailMode ? 'true' : null"
          :aria-expanded="isRailMode ? String(flyoutItem === item) : null"
          :data-dd-action-name="menuGroupActionName(item)"
          @click.prevent="onParentClick($event, item, index)"
        >
          <span class="icon">
            <img
              :src="getIcon(item.data ? item.data.icon : undefined)"
              :alt="getIcon(item.data ? item.data.icon : undefined)"
            />
          </span>
          <span class="menu-label">
            {{ item.text }}

            <svg width="14px" height="8px" viewBox="0 0 14 8" xmlns="http://www.w3.org/2000/svg" class="menu-arrow">
              <polyline
                fill="none"
                stroke="#FFFFFF"
                stroke-width="1.3"
                stroke-linecap="round"
                stroke-linejoin="round"
                points="1 7, 7 1, 13 7"
              />
            </svg>
          </span>
        </a>

        <router-link
          v-else
          :to="getLink(item)"
          exact
          :data-dd-action-name="menuActionName(item)"
          @click.native="onLinkClick(item)"
        >
          <span class="icon">
            <img
              :src="getIcon(item.data ? item.data.icon : undefined)"
              :alt="getIcon(item.data ? item.data.icon : undefined)"
            />
          </span>
          <span class="menu-label">{{ item.text }}</span>
        </router-link>

        <ul v-if="item.children" class="child-items second-level">
          <li v-for="(c, childIndex) in item.children" :key="`${childIndex}-${c.text}`">
            <router-link :to="getLink(c)" exact :data-dd-action-name="menuActionName(c)" @click.native.stop="onLinkClick">
              <span class="menu-label">{{ c.text }}</span>
            </router-link>
          </li>
        </ul>
      </li>
    </ul>

    <!-- Collapsed rail: the active parent's label and children, anchored next to its icon.
         Opened by hover or focus; Enter moves focus into it, Esc closes and returns. -->
    <div
      v-if="flyoutItem"
      ref="flyout"
      class="side-navigation-flyout"
      :style="flyoutStyle"
      role="group"
      :aria-label="flyoutItem.text"
      @mouseenter="cancelFlyoutClose"
      @mouseleave="scheduleFlyoutClose"
    >
      <router-link
        v-if="!flyoutItem.children"
        :to="getLink(flyoutItem)"
        exact
        class="flyout-title"
        :data-dd-action-name="menuActionName(flyoutItem)"
        @click.native="onFlyoutLinkClick(flyoutItem)"
      >
        {{ flyoutItem.text }}
      </router-link>
      <div v-else class="flyout-title">{{ flyoutItem.text }}</div>

      <ul v-if="flyoutItem.children" class="flyout-items">
        <li v-for="(c, index) in flyoutItem.children" :key="`${index}-${c.text}`">
          <router-link :to="getLink(c)" exact :data-dd-action-name="menuActionName(c)" @click.native="onFlyoutLinkClick(c)">
            {{ c.text }}
          </router-link>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Swal from 'sweetalert2';
import { isWithinInterval, parseISO } from 'date-fns';
import auth from '@/modules/Flexigo/services/auth';
import { menuItemRoute, menuLeaves, visibleMenuItems } from '@/modules/Flexigo/helpers/menu';
import { endRumSession } from '@/modules/Flexigo/helpers/rum';
import { viewName } from '@/modules/Flexigo/helpers/rum/viewName';

export default {
  name: 'SideMenu',
  data() {
    return {
      baseUrl: process.env.BASE_URL,
      onDemandMenuRoute: 'SharedRideRequest',
      // Collapsed rail flyout (opened by hover or by focus)
      flyoutItem: null,
      flyoutIndex: -1,
      flyoutTop: 0,
      flyoutLeft: 0,
      flyoutCloseTimer: null,
      isDesktop: window.innerWidth >= 768,
    };
  },
  computed: {
    items() {
      return this.$store.state.mainMenu;
    },
    menuChildren() {
      return visibleMenuItems(this.items, this.$store.state.companySettings);
    },
    isSideMenuVisible() {
      return this.$store.state.isSideMenuVisible;
    },
    isMainMenuCollapsed() {
      return this.$store.state.isMainMenuCollapsed;
    },
    // The rail only collapses on desktop; the mobile drawer keeps its full-width layout.
    isRailMode() {
      return this.isMainMenuCollapsed && this.isDesktop;
    },
    flyoutStyle() {
      return { top: `${this.flyoutTop}px`, left: `${this.flyoutLeft}px` };
    },
    fullName() {
      return this.$store.getters.userDetails.fullName;
    },
    email() {
      return this.$store.getters.userDetails.email;
    },
    modulePauseInfos() {
      return this.$store.state.companySettings?.modulePauseInfos || [];
    },
  },
  watch: {
    isRailMode() {
      this.closeFlyout();
    },
    $route() {
      this.closeFlyout();
    },
    modulePauseInfos() {
      if (window.location.hash.includes('shared-ride')) {
        const pausedModule = this.checkIfRoutePaused();

        if (pausedModule) {
          this.showPausedModulePopup(pausedModule);
        }
      }
    },
  },
  async mounted() {
    console.log('SideMenu mounted');
    window.addEventListener('resize', this.onWindowResize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onWindowResize);
    this.cancelFlyoutClose();
  },
  methods: {
    getIcon(iconName) {
      if (iconName) {
        return `${this.baseUrl}menu/${iconName}.svg`;
      }
      return `${this.baseUrl}menu/icon-flexicar.svg`;
    },
    // getIconSet(iconName) {
    //   if (iconName) {
    //     return `${this.baseUrl}menu/${iconName}.png, ${this.baseUrl}menu/${iconName}@2x.png, ${this.baseUrl}menu/${iconName}@3x.png`;
    //   }
    //   return '';
    // },
    getLink(link) {
      return menuItemRoute(link);
    },
    // Same identity as the Datadog view the link opens, so menu clicks and page views line up
    menuActionName(link) {
      return `menu:${viewName(menuItemRoute(link))}`;
    },
    // Group labels are per company and translated, and icons repeat, so a group is named by its first page
    menuGroupActionName(item) {
      const [first] = menuLeaves(item);
      return `menu-group:${first ? viewName(menuItemRoute(first)) : 'empty'}`;
    },
    onWindowResize() {
      this.isDesktop = window.innerWidth >= 768;
    },
    // Hover or focus on a rail item previews its label and children
    onItemActivate($event, item, index) {
      if (!this.isRailMode) return;
      if (this.flyoutItem === item) {
        this.cancelFlyoutClose();
        return;
      }
      this.openFlyout($event.currentTarget, item, index);
    },
    openFlyout(itemEl, item, index) {
      this.cancelFlyoutClose();

      // Anchor to the rail's own right edge so no dead gap sits between icon and panel
      this.flyoutTop = itemEl.getBoundingClientRect().top;
      this.flyoutLeft = this.$el.getBoundingClientRect().right;
      this.flyoutIndex = index;
      this.flyoutItem = item;

      this.$nextTick(this.clampFlyoutToViewport);
    },
    // Keep the panel inside the viewport once its real height is known
    clampFlyoutToViewport() {
      const el = this.$refs.flyout;
      if (!el) return;
      const maxTop = window.innerHeight - el.offsetHeight;
      if (this.flyoutTop > maxTop) this.flyoutTop = Math.max(0, maxTop);
    },
    // Short grace period so the pointer can travel from the icon onto the panel
    scheduleFlyoutClose() {
      if (!this.flyoutItem) return;
      this.cancelFlyoutClose();
      this.flyoutCloseTimer = setTimeout(() => {
        // A stray pointer must not pull the panel out from under a keyboard user
        if (this.hasFocusInsideFlyout()) return;
        this.closeFlyout();
      }, 150);
    },
    cancelFlyoutClose() {
      if (this.flyoutCloseTimer) {
        clearTimeout(this.flyoutCloseTimer);
        this.flyoutCloseTimer = null;
      }
    },
    closeFlyout() {
      this.cancelFlyoutClose();
      this.flyoutItem = null;
      this.flyoutIndex = -1;
    },
    // The panel renders outside the list (so the rail keeps its own scrolling), so its
    // trigger is looked up by position rather than held as a reactive DOM reference.
    getFlyoutTriggerItem() {
      if (this.flyoutIndex < 0) return null;
      return this.$el.querySelectorAll('.side-navigation-menu-items > li')[this.flyoutIndex] || null;
    },
    hasFocusInsideFlyout() {
      const active = document.activeElement;
      if (!active) return false;
      return !!(this.$refs.flyout?.contains(active) || this.getFlyoutTriggerItem()?.contains(active));
    },
    onNavFocusOut($event) {
      if (!this.flyoutItem) return;
      const next = $event.relatedTarget;
      // A null relatedTarget also covers focus lost to a re-render, which must not close the panel
      if (!next) return;
      if (this.$refs.flyout?.contains(next) || this.getFlyoutTriggerItem()?.contains(next)) return;
      this.closeFlyout();
    },
    onNavEscape() {
      if (!this.flyoutItem) return;
      const trigger = this.getFlyoutTriggerItem()?.querySelector('a');
      this.closeFlyout();
      if (trigger) trigger.focus();
    },
    focusFirstFlyoutLink() {
      const link = this.$refs.flyout?.querySelector('a');
      if (link) link.focus();
    },
    onFlyoutLinkClick(link) {
      this.closeFlyout();
      this.onLinkClick(link);
    },
    onParentClick($event, item, index) {
      // Collapsed rail: the labels are hidden, so activating a parent moves focus into its
      // flyout instead of toggling the (hidden) inline accordion. Esc closes and comes back.
      if (this.isRailMode) {
        $event.preventDefault();
        if (this.flyoutItem !== item) {
          this.openFlyout($event.currentTarget.parentElement, item, index);
        }
        this.$nextTick(this.focusFirstFlyoutLink);
        return;
      }
      console.log('Parent click triggered!', $event.currentTarget);
      console.log('Event target:', $event.target);
      console.log('Current target classes:', $event.currentTarget.className);
      $event.preventDefault();
      $event.currentTarget.classList.toggle('collapsed');
    },
    checkIfRoutePaused() {
      const moduleCode = 'flexiridepool';
      const pausedModule = this.modulePauseInfos.find((module) => module.moduleCode === moduleCode);

      if (!pausedModule) return null;

      const isInPauseRange = isWithinInterval(new Date(), {
        start: parseISO(pausedModule.pauseFrom),
        end: parseISO(pausedModule.pauseUntil),
      });

      return isInPauseRange ? pausedModule : null;
    },
    showPausedModulePopup(pausedModule) {
      Swal.fire({
        icon: 'warning',
        html: `<div class="swal-scrollable-content">${pausedModule.message}</div>`,
        confirmButtonText: 'OK',
      }).then(() => {
        this.$router.push({ name: 'legacy', params: { route: 'app.personel.notifications' } });
      });
    },
    onLinkClick(link) {
      if (link?.route === this.onDemandMenuRoute) {
        const pausedModule = this.checkIfRoutePaused();

        if (pausedModule) {
          this.showPausedModulePopup(pausedModule);
          return;
        }
      }

      if (window.innerWidth < 768) {
        this.$store.dispatch('SET_IS_SIDE_MENU_VISIBLE', false);
      }
    },
    toggleSideMenu() {
      this.$store.dispatch('toggleSideMenu');
    },
    // this is copied from topbar.vue
    // need to refactor this to a common place
    async logout() {
      try {
        const [base] = window.location.hostname.split('.');
        const urls = {
          localhost: 'http://localhost:8080/admin/flexigo/#/login',
          secure: 'https://secure.flexigo.com?lang=tr',
          portal: 'https://portal.flexigo.com?lang=en',
          secure3: 'https://secure3.vektorteknoloji.com/flexigo/',
          timezone: 'https://timezone.flexigo.com',
        };
        await auth.logout();
        endRumSession();
        sessionStorage.clear();
        localStorage.removeItem('flexigoAutoLoginContext');
        window.location.href = urls[base];
      } catch (error) {
        console.error(error);
      }
    },
  },
};
</script>

<style lang="scss">
.side-navigation-logout {
  padding: 0;
  margin: 64px 20px -30px 40px;
  color: white;
}
.swal-scrollable-content {
  max-height: 300px;
  overflow-y: auto;
}
</style>
